import React, { useState, useEffect, useCallback, useRef } from 'react';
import { createPortal } from 'react-dom';
import { useI18n, isRTL } from '../i18n';

// Icons
const ChevronRightIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="rtl:-scale-x-100">
    <polyline points="9 18 15 12 9 6" />
  </svg>
);

const ChevronLeftIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="rtl:-scale-x-100">
    <polyline points="15 18 9 12 15 6" />
  </svg>
);

const XIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <line x1="18" y1="6" x2="6" y2="18" />
    <line x1="6" y1="6" x2="18" y2="18" />
  </svg>
);

const CheckIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
    <polyline points="20 6 9 17 4 12" />
  </svg>
);

const PlayIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <polygon points="5 3 19 12 5 21 5 3" />
  </svg>
);

const SparklesIcon = () => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="m12 3-1.912 5.813a2 2 0 0 1-1.275 1.275L3 12l5.813 1.912a2 2 0 0 1 1.275 1.275L12 21l1.912-5.813a2 2 0 0 1 1.275-1.275L21 12l-5.813-1.912a2 2 0 0 1-1.275-1.275L12 3Z" />
  </svg>
);

// Dashboard tour step definitions
const DASHBOARD_TOUR_STEPS = [
  {
    id: 'welcome',
    type: 'modal',
    title: 'Welcome to ArabCap! 🎉',
    description: "Let's take a quick tour to help you get started with creating amazing Arabic subtitles for your videos.",
    details: 'This tour will show you how to navigate the dashboard, access your settings, and create your first project.',
    icon: <SparklesIcon />,
    iconBg: 'bg-gradient-to-br from-[#f73140] to-[#ff6b6b]',
  },
  {
    id: 'project-grid',
    type: 'spotlight',
    target: '[data-tour="project-grid"]',
    position: 'top',
    title: 'Your Projects',
    description: 'All your projects appear here in a beautiful grid view.',
    details: 'You can see project status (Completed, Processing, Draft), search by name, and click any project to open it in the editor. Processing projects show real-time progress.',
    icon: (
      <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
        <rect x="3" y="3" width="7" height="7" rx="1" />
        <rect x="14" y="3" width="7" height="7" rx="1" />
        <rect x="3" y="14" width="7" height="7" rx="1" />
        <rect x="14" y="14" width="7" height="7" rx="1" />
      </svg>
    ),
    iconBg: 'bg-blue-500',
  },
  {
    id: 'usage-info',
    type: 'spotlight',
    target: '[data-tour="usage-info"]',
    position: 'right',
    title: 'Your Plan & Usage',
    description: 'Track your video usage and plan limits here.',
    details: 'See how many videos you have left this month, your maximum video duration, and when your usage resets. Maximum video duration is currently 2 minutes for all plans.',
    icon: (
      <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
        <path d="M12 2v20M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6" />
      </svg>
    ),
    iconBg: 'bg-emerald-500',
    desktopOnly: true, // This step is only shown on desktop (sidebar not visible on mobile)
    mobileAlternative: {
      type: 'modal',
      title: 'Your Plan & Usage',
      description: 'You can access your plan info from the sidebar menu.',
      details: 'Open the menu (☰) to see how many videos you have left this month, your maximum video duration, and when your usage resets. Maximum video duration is currently 2 minutes for all plans.',
    },
  },
  {
    id: 'settings',
    type: 'spotlight',
    target: '[data-tour="settings-button"]',
    position: 'right',
    title: 'Settings',
    description: 'Access your profile and account settings.',
    details: 'Update your profile information, manage your subscription, and configure preferences. You can also sign out from here.',
    icon: (
      <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
        <circle cx="12" cy="12" r="3" />
        <path d="M19.4 15a1.7 1.7 0 0 0 .33 1.82l.06.06a2 2 0 0 1-2.83 2.83l-.06-.06a1.7 1.7 0 0 0-1.82-.33 1.7 1.7 0 0 0-1 1.54V21a2 2 0 0 1-4 0v-.09a1.7 1.7 0 0 0-1-1.54 1.7 1.7 0 0 0-1.82.33l-.06.06a2 2 0 1 1-2.83-2.83l.06-.06a1.7 1.7 0 0 0 .33-1.82 1.7 1.7 0 0 0-1.54-1H3a2 2 0 0 1 0-4h.09a1.7 1.7 0 0 0 1.54-1 1.7 1.7 0 0 0-.33-1.82l-.06-.06a2 2 0 1 1 2.83-2.83l.06.06a1.7 1.7 0 0 0 1.82.33H9a1.7 1.7 0 0 0 1-1.54V3a2 2 0 0 1 4 0v.09a1.7 1.7 0 0 0 1 1.54 1.7 1.7 0 0 0 1.82-.33l.06-.06a2 2 0 1 1 2.83 2.83l-.06.06a1.7 1.7 0 0 0-.33 1.82V9c0 .69.4 1.31 1.02 1.58.24.1.5.16.78.16H21a2 2 0 0 1 0 4h-.09a1.7 1.7 0 0 0-1.51 1.26z" />
      </svg>
    ),
    iconBg: 'bg-slate-600',
    desktopOnly: true, // This step is only shown on desktop (sidebar not visible on mobile)
    mobileAlternative: {
      type: 'modal',
      title: 'Settings',
      description: 'Access your settings from the sidebar menu.',
      details: 'Open the menu (☰) to find Settings where you can update your profile, manage your subscription, and configure preferences.',
    },
  },
  {
    id: 'new-project',
    type: 'spotlight',
    target: '[data-tour="new-project-button"]',
    position: 'bottom',
    title: 'Create Your First Project',
    description: 'Click this button to start creating your first subtitling project!',
    details: 'Go ahead and click "New Project" - we\'ll guide you through each step of the process.',
    icon: (
      <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
        <line x1="12" y1="5" x2="12" y2="19" />
        <line x1="5" y1="12" x2="19" y2="12" />
      </svg>
    ),
    iconBg: 'bg-[#f73140]',
    isClickToAction: true, // Special flag to indicate user should click the target
    actionButtonText: 'Click "New Project" to continue',
  },
];

// Project creation tour steps (shown when modal opens)
const PROJECT_CREATION_TOUR_STEPS = [
  {
    id: 'step-upload',
    type: 'info-card',
    step: 1,
    totalSteps: 5,
    title: 'Step 1: Upload Your Media',
    description: 'Drag and drop or browse to select your video or audio file.',
    details: 'We support MP4, MOV, and MP3 files up to 500MB. Maximum video duration is 2 minutes for all plans. We\'ll validate the duration before proceeding.',
    icon: (
      <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
        <path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4" />
        <polyline points="17 8 12 3 7 8" />
        <line x1="12" y1="3" x2="12" y2="15" />
      </svg>
    ),
    iconBg: 'bg-[#f73140]',
    tip: 'Pro tip: For best results, use clear audio with minimal background noise.',
  },
  {
    id: 'step-language',
    type: 'info-card',
    step: 2,
    totalSteps: 5,
    title: 'Step 2: Choose Your Language',
    description: 'Select which Arabic dialect is spoken in your content.',
    details: 'We currently support Moroccan Darija, Egyptian, Algerian, Tunisian, and Gulf Arabic. Our AI is specially trained on real dialects, not just formal Arabic.',
    icon: (
      <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
        <circle cx="12" cy="12" r="10" />
        <line x1="2" y1="12" x2="22" y2="12" />
        <path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z" />
      </svg>
    ),
    iconBg: 'bg-blue-500',
    tip: 'More dialects coming soon including Levantine and Sudanese!',
  },
  {
    id: 'step-script',
    type: 'info-card',
    step: 3,
    totalSteps: 5,
    title: 'Step 3: Script Style',
    description: 'Choose how you want your subtitles written.',
    details: 'Standard Arabic uses Arabic script (عربي). Arabizi (Latin letters like 3arbi) is coming soon for a more casual style that matches how people type on social media.',
    icon: (
      <span className="text-2xl font-bold font-serif">ض</span>
    ),
    iconBg: 'bg-amber-500',
    tip: 'Arabic script is perfect for professional and social media content.',
  },
  {
    id: 'step-foreign',
    type: 'info-card',
    step: 4,
    totalSteps: 5,
    title: 'Step 4: Foreign Words',
    description: 'Tell us how to handle words from other languages.',
    details: 'When you say "عندي meeting" in your video, we can: write it in Arabic letters (ميتينغ), keep the original (meeting), or translate it (اجتماع). You can change this later in the editor!',
    icon: (
      <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
        <path d="M5 8l6 6" />
        <path d="M4 14l6-6 2-3" />
        <path d="M2 5h12" />
        <path d="M7 2h1" />
        <path d="M22 22l-5-10-5 10" />
        <path d="M14 18h6" />
      </svg>
    ),
    iconBg: 'bg-purple-500',
    tip: 'Transliterated works best for casual content like vlogs and social media.',
  },
  {
    id: 'step-name',
    type: 'info-card',
    step: 5,
    totalSteps: 5,
    title: 'Step 5: Name & Advanced Settings',
    description: 'Give your project a name and optionally tweak advanced settings.',
    details: 'Name your project (we suggest the file name). Advanced settings let you control words per chunk, split behavior, and more. These are optional - the defaults work great!',
    icon: <SparklesIcon />,
    iconBg: 'bg-emerald-500',
    tip: 'You can always adjust chunk settings later in the editor.',
  },
  {
    id: 'complete',
    type: 'modal',
    title: "You're Ready! 🚀",
    description: "Now you know all the steps to create a project.",
    details: 'Go ahead and upload your first video! Our AI will transcribe it with high accuracy. You can close this guide and start creating.',
    icon: <CheckIcon />,
    iconBg: 'bg-gradient-to-br from-emerald-400 to-emerald-600',
  },
];

// Spotlight overlay component that highlights a target element
const SpotlightOverlay = ({ targetRect, padding = 12, borderRadius = 24 }) => {
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  
  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  if (!targetRect) return null;

  const { top, left, width, height } = targetRect;
  const spotlightTop = top - padding;
  const spotlightLeft = left - padding;
  const spotlightWidth = width + padding * 2;
  const spotlightHeight = height + padding * 2;
  
  // Less opaque on mobile for better visibility
  const overlayOpacity = isMobile ? 0.5 : 0.75;

  return (
    <div className="fixed inset-0 z-[9998] pointer-events-none">
      <svg className="w-full h-full">
        <defs>
          <mask id="spotlight-mask">
            <rect width="100%" height="100%" fill="white" />
            <rect
              x={spotlightLeft}
              y={spotlightTop}
              width={spotlightWidth}
              height={spotlightHeight}
              rx={borderRadius}
              fill="black"
            />
          </mask>
        </defs>
        <rect
          width="100%"
          height="100%"
          fill={`rgba(15, 23, 42, ${overlayOpacity})`}
          mask="url(#spotlight-mask)"
          className="transition-all duration-150"
        />
      </svg>
      {/* Glowing border around spotlight */}
      <div
        className="absolute border-2 border-white/50 shadow-[0_0_0_4px_rgba(247,49,64,0.3),0_0_30px_rgba(247,49,64,0.4)] transition-all duration-150"
        style={{
          top: spotlightTop,
          left: spotlightLeft,
          width: spotlightWidth,
          height: spotlightHeight,
          borderRadius: borderRadius,
        }}
      />
    </div>
  );
};

// Tooltip/Card component for tour content
const TourCard = ({ 
  step, 
  currentIndex, 
  totalSteps, 
  onNext, 
  onPrev, 
  onSkip, 
  onComplete,
  onClickAction,
  position = 'bottom',
  targetRect 
}) => {
  const { t, isRTL: rtl } = useI18n();
  const cardRef = useRef(null);
  const [cardPosition, setCardPosition] = useState({ top: 0, left: 0 });
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);

  // Check for mobile on resize
  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Calculate card position based on target element (desktop only)
  useEffect(() => {
    if (!cardRef.current || isMobile) return;
    
    const cardRect = cardRef.current.getBoundingClientRect();
    const cardWidth = cardRect.width;
    const cardHeight = cardRect.height;
    const margin = 20;

    let top, left;

    if (step.type === 'modal' || step.type === 'info-card' || !targetRect) {
      // Center in viewport
      top = (window.innerHeight - cardHeight) / 2;
      left = (window.innerWidth - cardWidth) / 2;
    } else {
      // Position relative to target
      // Mirror side placements in right-to-left layouts
      const side = rtl && (position === 'left' || position === 'right')
        ? (position === 'left' ? 'right' : 'left')
        : position;
      switch (side) {
        case 'top':
          top = targetRect.top - cardHeight - margin;
          left = targetRect.left + (targetRect.width - cardWidth) / 2;
          break;
        case 'bottom':
          top = targetRect.bottom + margin;
          left = targetRect.left + (targetRect.width - cardWidth) / 2;
          break;
        case 'left':
          top = targetRect.top + (targetRect.height - cardHeight) / 2;
          left = targetRect.left - cardWidth - margin;
          break;
        case 'right':
          top = targetRect.top + (targetRect.height - cardHeight) / 2;
          left = targetRect.right + margin;
          break;
        default:
          top = targetRect.bottom + margin;
          left = targetRect.left + (targetRect.width - cardWidth) / 2;
      }

      // Keep within viewport bounds
      const padding = 20;
      if (left < padding) left = padding;
      if (left + cardWidth > window.innerWidth - padding) {
        left = window.innerWidth - cardWidth - padding;
      }
      if (top < padding) top = padding;
      if (top + cardHeight > window.innerHeight - padding) {
        top = window.innerHeight - cardHeight - padding;
      }
    }

    setCardPosition({ top, left });
  }, [step, position, targetRect, isMobile, rtl]);

  const isFirstStep = currentIndex === 0;
  const isLastStep = currentIndex === totalSteps - 1;
  const isClickToAction = step.isClickToAction;

  // Mobile bottom sheet version
  if (isMobile) {
    return (
      <div className="fixed z-[10000] inset-x-0 bottom-0 animate-[slideUp_0.15s_ease-out]">
        <div className="bg-white rounded-t-3xl shadow-2xl border-t border-slate-100 overflow-hidden max-h-[80vh] overflow-y-auto">
          {/* Handle bar */}
          <div className="flex justify-center pt-3 pb-2">
            <div className="w-10 h-1 bg-slate-200 rounded-full" />
          </div>
          
          {/* Progress bar */}
          <div className="h-1 bg-slate-100 mx-4 rounded-full overflow-hidden">
            <div
              className="h-full bg-gradient-to-r from-[#f73140] to-[#ff6b6b] transition-all duration-150 ease-out rounded-full"
              style={{ width: `${((currentIndex + 1) / totalSteps) * 100}%` }}
            />
          </div>

          {/* Header */}
          <div className="p-4 pb-3">
            <div className="flex items-start gap-3">
              {/* Icon */}
              <div className={`w-10 h-10 rounded-xl ${step.iconBg} flex items-center justify-center text-white shrink-0 shadow-lg`}>
                {step.icon}
              </div>
              
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 mb-1">
                  <span className="text-[10px] font-bold text-[#f73140] uppercase tracking-wider">
                    <bdi dir="ltr">{currentIndex + 1}/{totalSteps}</bdi>
                  </span>
                </div>
                <h3 className="text-lg font-bold text-slate-900">{t(step.title)}</h3>
                <p className="text-slate-600 text-sm">{t(step.description)}</p>
              </div>

              {/* Skip button */}
              <button
                onClick={onSkip}
                className="p-2 hover:bg-slate-100 rounded-lg transition-colors text-slate-400 hover:text-slate-700 shrink-0"
                aria-label={t('Skip tour')}
              >
                <XIcon />
              </button>
            </div>
          </div>

          {/* Details section */}
          <div className="px-4 pb-3">
            <div className="p-3 bg-slate-50 rounded-xl border border-slate-100">
              <p className="text-sm text-slate-600 leading-relaxed">{t(step.details)}</p>
            </div>
            
            {/* Pro tip */}
            {step.tip && (
              <div className="mt-2 flex items-start gap-2 p-2.5 bg-amber-50 rounded-xl border border-amber-100">
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#d97706" strokeWidth="2" className="shrink-0 mt-0.5">
                  <circle cx="12" cy="12" r="10" />
                  <path d="M12 16v-4" />
                  <path d="M12 8h.01" />
                </svg>
                <p className="text-xs text-amber-700 font-medium">{t(step.tip)}</p>
              </div>
            )}
          </div>

          {/* Navigation */}
          <div className="px-4 pb-6 pt-2 flex items-center gap-2">
            {/* Skip tour button */}
            <button
              onClick={onSkip}
              className="flex items-center justify-center gap-1 px-3 py-3 rounded-xl text-xs font-bold text-slate-400 hover:text-slate-700 hover:bg-slate-50 transition-colors"
            >
              {t('Skip tour')}
            </button>

            {!isFirstStep && !isClickToAction && (
              <button
                onClick={onPrev}
                className="flex items-center justify-center gap-1 px-4 py-3 rounded-xl text-sm font-bold text-slate-500 bg-slate-100"
              >
                <ChevronLeftIcon />
                {t('Back')}
              </button>
            )}
            
            {isClickToAction ? (
              <button
                onClick={onClickAction}
                className="flex items-center justify-center gap-2 px-4 py-3 rounded-xl text-sm font-bold bg-gradient-to-r from-[#f73140] to-[#ff6b6b] text-white shadow-lg flex-1 animate-pulse"
              >
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M15 15l-2 5L9 9l11 4-5 2zm0 0l5 5" />
                </svg>
                {t(step.actionButtonText || 'Click to continue')}
              </button>
            ) : isLastStep ? (
              <button
                onClick={onComplete}
                className="flex items-center justify-center gap-2 px-4 py-3 rounded-xl text-sm font-bold bg-gradient-to-r from-[#f73140] to-[#ff6b6b] text-white shadow-lg flex-1"
              >
                <CheckIcon />
                {t('Got it!')}
              </button>
            ) : (
              <button
                onClick={onNext}
                className="flex items-center justify-center gap-2 px-4 py-3 rounded-xl text-sm font-bold bg-[#f73140] text-white shadow-lg flex-1"
              >
                {t('Next')}
                <ChevronRightIcon />
              </button>
            )}
          </div>
        </div>

        <style>{`
          @keyframes slideUp {
            from { opacity: 0; transform: translateY(100%); }
            to { opacity: 1; transform: translateY(0); }
          }
        `}</style>
      </div>
    );
  }

  // Desktop version
  return (
    <div
      ref={cardRef}
      className="fixed z-[10000] w-[400px] max-w-[calc(100vw-40px)] bg-white rounded-3xl shadow-2xl border border-white/50 overflow-hidden animate-[scaleUp_0.16s_cubic-bezier(0.16,1,0.3,1)]"
      style={{ top: cardPosition.top, left: cardPosition.left }}
      dir={rtl ? 'rtl' : 'ltr'}
    >
      {/* Progress bar */}
      <div className="h-1 bg-slate-100">
        <div
          className="h-full bg-gradient-to-r from-[#f73140] to-[#ff6b6b] transition-all duration-150 ease-out"
          style={{ width: `${((currentIndex + 1) / totalSteps) * 100}%` }}
        />
      </div>

      {/* Header */}
      <div className="p-6 pb-4">
        <div className="flex items-start gap-4">
          {/* Icon */}
          <div className={`w-12 h-12 rounded-2xl ${step.iconBg} flex items-center justify-center text-white shrink-0 shadow-lg`}>
            {step.icon}
          </div>
          
          <div className="flex-1 min-w-0">
            {/* Step indicator for info-cards */}
            {step.type === 'info-card' && (
              <div className="flex items-center gap-2 mb-2">
                <span className="text-[10px] font-bold text-[#f73140] uppercase tracking-wider">
                  {t('Step {n} of {total}', { n: step.step, total: step.totalSteps })}
                </span>
                <div className="flex gap-1">
                  {Array.from({ length: step.totalSteps }).map((_, i) => (
                    <div
                      key={i}
                      className={`w-1.5 h-1.5 rounded-full transition-colors ${
                        i + 1 <= step.step ? 'bg-[#f73140]' : 'bg-slate-200'
                      }`}
                    />
                  ))}
                </div>
              </div>
            )}
            
            <h3 className="text-xl font-bold text-slate-900 mb-1">{t(step.title)}</h3>
            <p className="text-slate-600 text-sm leading-relaxed">{t(step.description)}</p>
          </div>

          {/* Skip button */}
          <button
            onClick={onSkip}
            className="p-2 hover:bg-slate-100 rounded-xl transition-colors text-slate-400 hover:text-slate-700 shrink-0"
            aria-label={t('Skip tour')}
          >
            <XIcon />
          </button>
        </div>
      </div>

      {/* Details section */}
      <div className="px-6 pb-4">
        <div className="p-4 bg-slate-50 rounded-2xl border border-slate-100">
          <p className="text-sm text-slate-600 leading-relaxed">{t(step.details)}</p>
        </div>
        
        {/* Pro tip */}
        {step.tip && (
          <div className="mt-3 flex items-start gap-2 p-3 bg-amber-50 rounded-xl border border-amber-100">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#d97706" strokeWidth="2" className="shrink-0 mt-0.5">
              <circle cx="12" cy="12" r="10" />
              <path d="M12 16v-4" />
              <path d="M12 8h.01" />
            </svg>
            <p className="text-xs text-amber-700 font-medium">{t(step.tip)}</p>
          </div>
        )}
      </div>

      {/* Navigation */}
      <div className="px-6 pb-6 flex items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          <div className="flex items-center gap-1 text-sm text-slate-400" dir="ltr">
            <span className="font-bold text-slate-600">{currentIndex + 1}</span>
            <span>/</span>
            <span>{totalSteps}</span>
          </div>
          {/* Skip tour button */}
          <button
            onClick={onSkip}
            className="text-xs font-bold text-slate-400 hover:text-slate-700 transition-colors px-2 py-1 rounded-lg hover:bg-slate-50"
          >
            {t('Skip tour')}
          </button>
        </div>

        <div className="flex items-center gap-2">
          {!isFirstStep && !isClickToAction && (
            <button
              onClick={onPrev}
              className="flex items-center gap-1 px-4 py-2.5 rounded-xl text-sm font-bold text-slate-500 hover:bg-slate-100 transition-colors"
            >
              <ChevronLeftIcon />
              {t('Back')}
            </button>
          )}
          
          {isClickToAction ? (
            <button
              onClick={onClickAction}
              className="flex items-center gap-2 px-6 py-2.5 rounded-xl text-sm font-bold bg-gradient-to-r from-[#f73140] to-[#ff6b6b] text-white shadow-lg shadow-[#f73140]/25 hover:shadow-xl hover:shadow-[#f73140]/30 transition-all animate-pulse"
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M15 15l-2 5L9 9l11 4-5 2zm0 0l5 5M7.188 2.239l.777 2.897M5.136 7.965l-2.898-.777M13.95 4.05l-2.122 2.122m-5.657 5.656l-2.12 2.122" />
              </svg>
              {t(step.actionButtonText || 'Click to continue')}
            </button>
          ) : isLastStep ? (
            <button
              onClick={onComplete}
              className="flex items-center gap-2 px-6 py-2.5 rounded-xl text-sm font-bold bg-gradient-to-r from-[#f73140] to-[#ff6b6b] text-white shadow-lg shadow-[#f73140]/25 hover:shadow-xl hover:shadow-[#f73140]/30 transition-all"
            >
              <CheckIcon />
              {t('Got it!')}
            </button>
          ) : (
            <button
              onClick={onNext}
              className="flex items-center gap-2 px-6 py-2.5 rounded-xl text-sm font-bold bg-[#f73140] text-white shadow-lg shadow-[#f73140]/25 hover:bg-[#d62331] transition-all"
            >
              {t('Next')}
              <ChevronRightIcon />
            </button>
          )}
        </div>
      </div>

      <style>{`
        @keyframes scaleUp {
          from { opacity: 0; transform: scale(0.95) translateY(10px); }
          to { opacity: 1; transform: scale(1) translateY(0); }
        }
      `}</style>
    </div>
  );
};

// Main Dashboard Tour Component
const DashboardTour = ({ isOpen, onComplete, onSkip, onNewProjectClick }) => {
  const [currentStep, setCurrentStep] = useState(0);
  const [targetRect, setTargetRect] = useState(null);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 1024);

  // Check for mobile on resize
  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 1024);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const rawStep = DASHBOARD_TOUR_STEPS[currentStep];
  
  // On mobile, use mobileAlternative if step is desktopOnly
  const step = (isMobile && rawStep.desktopOnly && rawStep.mobileAlternative)
    ? { ...rawStep, ...rawStep.mobileAlternative }
    : rawStep;

  // Reset step when tour opens
  useEffect(() => {
    if (isOpen) {
      setCurrentStep(0);
    }
  }, [isOpen]);

  // Find and highlight target element (only on desktop or for non-desktopOnly steps)
  const updateTargetRect = useCallback(() => {
    // Don't try to spotlight on mobile for desktopOnly steps
    if (isMobile && rawStep.desktopOnly) {
      setTargetRect(null);
      return;
    }
    
    if (step.type === 'spotlight' && step.target) {
      const element = document.querySelector(step.target);
      if (element) {
        const rect = element.getBoundingClientRect();
        setTargetRect(rect);
        // Scroll element into view if needed
        element.scrollIntoView({ behavior: 'auto', block: 'center' });
      } else {
        setTargetRect(null);
      }
    } else {
      setTargetRect(null);
    }
  }, [step, rawStep, isMobile]);

  useEffect(() => {
    if (isOpen) {
      updateTargetRect();
      // Update on scroll/resize
      window.addEventListener('scroll', updateTargetRect, true);
      window.addEventListener('resize', updateTargetRect);
      return () => {
        window.removeEventListener('scroll', updateTargetRect, true);
        window.removeEventListener('resize', updateTargetRect);
      };
    }
  }, [isOpen, currentStep, updateTargetRect]);

  // Keyboard navigation
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (!isOpen) return;
      if (e.key === 'Escape') {
        handleSkip();
      } else if (e.key === (isRTL() ? 'ArrowLeft' : 'ArrowRight') || e.key === 'Enter') {
        if (!step.isClickToAction) {
          handleNext();
        }
      } else if (e.key === (isRTL() ? 'ArrowRight' : 'ArrowLeft')) {
        handlePrev();
      }
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, currentStep, step]);

  const handleNext = () => {
    if (currentStep < DASHBOARD_TOUR_STEPS.length - 1) {
      setCurrentStep(currentStep + 1);
    }
  };

  const handlePrev = () => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
    }
  };

  const handleSkip = () => {
    // Save that user has seen the tour (skipped)
    try {
      localStorage.setItem('arabcap_tour_skipped', 'true');
    } catch (e) {}
    setCurrentStep(0);
    onSkip?.();
  };

  const handleComplete = () => {
    // Save that user has completed the dashboard tour
    try {
      localStorage.setItem('arabcap_tour_completed', 'true');
    } catch (e) {}
    setCurrentStep(0);
    onComplete?.();
  };

  const handleClickAction = () => {
    // Close the dashboard tour and trigger the new project action
    try {
      localStorage.setItem('arabcap_tour_completed', 'true');
    } catch (e) {}
    setCurrentStep(0);
    onNewProjectClick?.();
  };

  if (!isOpen) return null;

  const content = (
    <>
      {/* Dark overlay for modal-type steps, spotlight for spotlight-type */}
      {/* Less opaque on mobile for better visibility */}
      {step.type === 'spotlight' ? (
        <SpotlightOverlay targetRect={targetRect} />
      ) : (
        <div 
          className={`fixed inset-0 z-[9998] backdrop-blur-sm ${isMobile ? 'bg-slate-900/50' : 'bg-slate-900/70'}`}
          onClick={onSkip}
        />
      )}

      {/* Overlay layer - click outside to skip on modal steps, advance on spotlight steps */}
      <div 
        className="fixed inset-0 z-[9999]"
        onClick={(e) => {
          if (e.target === e.currentTarget) {
            if (step.type === 'modal') {
              onSkip();
            }
          }
        }}
      />

      {/* Tour card */}
      <TourCard
        step={step}
        currentIndex={currentStep}
        totalSteps={DASHBOARD_TOUR_STEPS.length}
        onNext={handleNext}
        onPrev={handlePrev}
        onSkip={handleSkip}
        onComplete={handleComplete}
        onClickAction={handleClickAction}
        position={step.position}
        targetRect={targetRect}
      />
    </>
  );

  return createPortal(content, document.body);
};

// Project Creation Guide Component (shown alongside the modal)
// This component shows contextual help as the user progresses through the modal
const ProjectCreationTour = ({ isOpen, modalStep, onClose }) => {
  const { t, isRTL: rtl } = useI18n();
  // Map modal steps to guide content
  const guideSteps = {
    1: {
      title: 'Upload Your Media',
      description: 'Drag and drop or click to browse for your video or audio file.',
      details: 'We support MP4, MOV, and MP3 files up to 500MB. Maximum video duration is currently 2 minutes for all plans.',
      tip: 'For best results, use clear audio with minimal background noise.',
      icon: (
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4" />
          <polyline points="17 8 12 3 7 8" />
          <line x1="12" y1="3" x2="12" y2="15" />
        </svg>
      ),
      iconBg: 'bg-[#f73140]',
    },
    2: {
      title: 'Choose Your Language',
      description: 'Select the Arabic dialect spoken in your content.',
      details: 'Our AI is specially trained on real dialects like Darija, Egyptian, and Gulf Arabic - not just formal Arabic.',
      tip: 'More dialects coming soon including Levantine and Sudanese!',
      icon: (
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <circle cx="12" cy="12" r="10" />
          <line x1="2" y1="12" x2="22" y2="12" />
          <path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z" />
        </svg>
      ),
      iconBg: 'bg-blue-500',
    },
    3: {
      title: 'Script Style',
      description: 'Choose how your subtitles will be written.',
      details: 'Standard Arabic uses Arabic script (عربي). Arabizi (Latin letters like 3arbi) is coming soon!',
      tip: 'Arabic script works great for professional and social media content.',
      icon: <span className="text-lg font-bold font-serif">ض</span>,
      iconBg: 'bg-amber-500',
    },
    4: {
      title: 'Foreign Words',
      description: 'How should we handle words from other languages?',
      details: 'When you say "عندي meeting", we can write it in Arabic letters (ميتينغ), keep the original (meeting), or translate it (اجتماع).',
      tip: 'You can change this setting later in the editor for individual words.',
      icon: (
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <path d="M5 8l6 6" />
          <path d="M4 14l6-6 2-3" />
          <path d="M2 5h12" />
          <path d="M7 2h1" />
          <path d="M22 22l-5-10-5 10" />
          <path d="M14 18h6" />
        </svg>
      ),
      iconBg: 'bg-purple-500',
    },
    5: {
      title: 'Almost Done!',
      description: 'Name your project and optionally adjust advanced settings.',
      details: 'The default settings work great for most videos. Advanced options let you control subtitle chunking and split behavior.',
      tip: 'You can always adjust these settings later in the editor.',
      icon: (
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <path d="m12 3-1.912 5.813a2 2 0 0 1-1.275 1.275L3 12l5.813 1.912a2 2 0 0 1 1.275 1.275L12 21l1.912-5.813a2 2 0 0 1 1.275-1.275L21 12l-5.813-1.912a2 2 0 0 1-1.275-1.275L12 3Z" />
        </svg>
      ),
      iconBg: 'bg-emerald-500',
    },
    6: {
      title: 'Processing...',
      description: 'Your video is being transcribed!',
      details: 'Our AI is analyzing your audio and generating accurate Arabic subtitles. This usually takes 1-3 minutes.',
      tip: 'You can close this and check back later - we\'ll keep processing in the background.',
      icon: (
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="animate-spin">
          <path d="M21 12a9 9 0 1 1-6.219-8.56" />
        </svg>
      ),
      iconBg: 'bg-violet-500',
    },
  };

  const currentGuide = guideSteps[modalStep];

  if (!isOpen || !currentGuide) return null;

  const content = (
    <>
      {/* Desktop version - positioned to the left of modal */}
      <div 
        className="fixed z-[10000] hidden lg:block"
        style={{
          ...(rtl ? { left: 'calc(50% + 300px)' } : { right: 'calc(50% + 300px)' }),
          top: '50%',
          transform: 'translateY(-50%)',
        }}
      >
        <div className="w-[280px] bg-white rounded-2xl shadow-2xl border border-slate-100 overflow-hidden animate-[slideInLeft_0.16s_ease-out]">
          {/* Header with step indicator */}
          <div className="bg-gradient-to-r from-slate-900 to-slate-800 px-5 py-4">
            <div className="flex items-center justify-between mb-2">
              <div className="flex items-center gap-2">
                <div className={`w-8 h-8 rounded-xl ${currentGuide.iconBg} flex items-center justify-center text-white shadow-lg`}>
                  {currentGuide.icon}
                </div>
                <span className="text-xs font-bold text-slate-400 uppercase tracking-wider">
                  {t('Step {n} of {total}', { n: modalStep, total: 5 })}
                </span>
              </div>
              <button
                onClick={onClose}
                className="p-1.5 hover:bg-white/10 rounded-lg transition-colors text-slate-400 hover:text-white"
              >
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <line x1="18" y1="6" x2="6" y2="18" />
                  <line x1="6" y1="6" x2="18" y2="18" />
                </svg>
              </button>
            </div>
            <h3 className="text-lg font-bold text-white">{t(currentGuide.title)}</h3>
          </div>

          {/* Content */}
          <div className="p-5">
            <p className="text-slate-700 text-sm font-medium mb-3">{t(currentGuide.description)}</p>
            
            <div className="p-3 bg-slate-50 rounded-xl border border-slate-100 mb-3">
              <p className="text-xs text-slate-600 leading-relaxed">{t(currentGuide.details)}</p>
            </div>

            {currentGuide.tip && (
              <div className="flex items-start gap-2 p-3 bg-amber-50 rounded-xl border border-amber-100">
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#d97706" strokeWidth="2" className="shrink-0 mt-0.5">
                  <circle cx="12" cy="12" r="10" />
                  <path d="M12 16v-4" />
                  <path d="M12 8h.01" />
                </svg>
                <p className="text-[11px] text-amber-700 font-medium leading-relaxed">{t(currentGuide.tip)}</p>
              </div>
            )}
          </div>

          {/* Progress dots */}
          <div className="px-5 pb-4 flex items-center justify-center gap-1.5">
            {[1, 2, 3, 4, 5].map((step) => (
              <div
                key={step}
                className={`w-2 h-2 rounded-full transition-all duration-150 ${
                  step === modalStep 
                    ? 'bg-[#f73140] w-4' 
                    : step < modalStep 
                      ? 'bg-[#f73140]/40' 
                      : 'bg-slate-200'
                }`}
              />
            ))}
          </div>
        </div>

        {/* Connector arrow pointing to modal */}
        <div className="absolute top-1/2 -end-3 transform -translate-y-1/2">
          <svg width="12" height="24" viewBox="0 0 12 24" fill="none" className="rtl:-scale-x-100">
            <path d="M0 0L12 12L0 24" fill="white" />
            <path d="M0 0L12 12L0 24" stroke="#e2e8f0" strokeWidth="1" fill="none" />
          </svg>
        </div>
      </div>

      {/* Mobile version - compact bar at top */}
      <div className="fixed z-[10001] lg:hidden top-0 left-0 right-0 animate-[slideDown_0.16s_ease-out]">
        <div className="bg-gradient-to-r from-slate-900 to-slate-800 px-4 py-3 shadow-xl safe-area-top">
          <div className="flex items-center gap-3">
            {/* Icon */}
            <div className={`w-10 h-10 rounded-xl ${currentGuide.iconBg} flex items-center justify-center text-white shadow-lg shrink-0`}>
              {currentGuide.icon}
            </div>
            
            {/* Content */}
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 mb-0.5">
                <span className="text-[10px] font-bold text-slate-400 uppercase tracking-wider">
                  {t('Step {n} of {total}', { n: modalStep, total: 5 })}
                </span>
                {/* Progress dots */}
                <div className="flex gap-1">
                  {[1, 2, 3, 4, 5].map((step) => (
                    <div
                      key={step}
                      className={`w-1.5 h-1.5 rounded-full ${
                        step === modalStep 
                          ? 'bg-[#f73140]' 
                          : step < modalStep 
                            ? 'bg-[#f73140]/40' 
                            : 'bg-slate-600'
                      }`}
                    />
                  ))}
                </div>
              </div>
              <h3 className="text-sm font-bold text-white truncate">{t(currentGuide.title)}</h3>
              <p className="text-xs text-slate-400 truncate">{t(currentGuide.description)}</p>
            </div>

            {/* Close button */}
            <button
              onClick={onClose}
              className="p-2 hover:bg-white/10 rounded-lg transition-colors text-slate-400 hover:text-white shrink-0"
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <line x1="18" y1="6" x2="6" y2="18" />
                <line x1="6" y1="6" x2="18" y2="18" />
              </svg>
            </button>
          </div>
          
          {/* Tip on mobile */}
          {currentGuide.tip && (
            <div className="mt-2 flex items-center gap-2 px-2 py-1.5 bg-amber-500/20 rounded-lg">
              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="#fbbf24" strokeWidth="2" className="shrink-0">
                <circle cx="12" cy="12" r="10" />
                <path d="M12 16v-4" />
                <path d="M12 8h.01" />
              </svg>
              <p className="text-[11px] text-amber-200 font-medium truncate">{t(currentGuide.tip)}</p>
            </div>
          )}
        </div>
      </div>

      <style>{`
        @keyframes slideInLeft {
          from { opacity: 0; transform: translateX(-20px) translateY(-50%); }
          to { opacity: 1; transform: translateX(0) translateY(-50%); }
        }
        @keyframes slideDown {
          from { opacity: 0; transform: translateY(-100%); }
          to { opacity: 1; transform: translateY(0); }
        }
      `}</style>
    </>
  );

  return createPortal(content, document.body);
};

// Tour trigger button component - icon only on mobile, text on desktop
export const TourTriggerButton = ({ onClick }) => {
  const { t } = useI18n();
  return (
  <button
    onClick={onClick}
    className="flex items-center justify-center gap-2 px-2.5 sm:px-4 py-2.5 rounded-xl text-sm font-bold text-slate-600 hover:text-[#f73140] bg-white hover:bg-slate-50 border border-slate-200 hover:border-[#f73140]/30 transition-all shadow-sm"
  >
    <PlayIcon />
    <span className="hidden sm:inline">{t('Take a Tour')}</span>
  </button>
  );
};

// Helper to check if dashboard tour should auto-show
export const shouldShowTour = () => {
  try {
    const completed = localStorage.getItem('arabcap_tour_completed');
    const skipped = localStorage.getItem('arabcap_tour_skipped');
    return !completed && !skipped;
  } catch {
    return false;
  }
};

// Helper to check if project creation guide should show
export const shouldShowProjectTour = () => {
  try {
    const dismissed = localStorage.getItem('arabcap_project_guide_dismissed');
    return !dismissed;
  } catch {
    return false;
  }
};

// Helper to dismiss project creation guide
export const dismissProjectTour = () => {
  try {
    localStorage.setItem('arabcap_project_guide_dismissed', 'true');
  } catch (e) {}
};

// Helper to reset tour state (for testing)
export const resetTourState = () => {
  try {
    localStorage.removeItem('arabcap_tour_completed');
    localStorage.removeItem('arabcap_tour_skipped');
    localStorage.removeItem('arabcap_project_guide_dismissed');
    localStorage.removeItem('arabcap_editor_tour_completed');
    localStorage.removeItem('arabcap_editor_tour_skipped');
    localStorage.removeItem('arabcap_settings_tour_completed');
  } catch (e) {}
};

// ============================================
// EDITOR TOUR
// ============================================

const EDITOR_TOUR_STEPS = [
  {
    id: 'welcome',
    type: 'modal',
    title: 'Welcome to the Editor! ✨',
    description: 'This is where you review and perfect your transcription.',
    details: 'Your video has been transcribed. Now you can edit words, adjust timing, and export in various formats. Let us show you around!',
    icon: (
      <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
        <path d="M11 4H4a2 2 0 00-2 2v14a2 2 0 002 2h14a2 2 0 002-2v-7" />
        <path d="M18.5 2.5a2.121 2.121 0 013 3L12 15l-4 1 1-4 9.5-9.5z" />
      </svg>
    ),
    iconBg: 'bg-gradient-to-br from-[#f73140] to-[#ff6b6b]',
    tip: 'This tour takes about 1 minute.',
  },
  {
    id: 'view-modes',
    type: 'spotlight',
    target: '[data-tour="view-mode-toggle"]',
    position: 'bottom',
    title: 'View Modes',
    description: 'Switch between Simple and Studio views.',
    details: 'Simple view shows a continuous prose for quick reading. Studio view shows word-by-word chunks for detailed editing with timing controls.',
    icon: (
      <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
        <rect x="3" y="3" width="7" height="7" />
        <rect x="14" y="3" width="7" height="7" />
        <rect x="3" y="14" width="7" height="7" />
        <rect x="14" y="14" width="7" height="7" />
      </svg>
    ),
    iconBg: 'bg-indigo-500',
    tip: 'Try Simple view for reviewing, Studio view for precise edits.',
  },
  {
    id: 'undo-redo',
    type: 'spotlight',
    target: '[data-tour="undo-redo"]',
    position: 'bottom',
    title: 'Undo & Redo',
    description: 'Made a mistake? No worries!',
    details: 'All your changes are tracked. Use Undo (Ctrl+Z) and Redo (Ctrl+Shift+Z) to navigate through your edit history.',
    icon: (
      <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
        <path d="M3 7v6h6" />
        <path d="M21 17a9 9 0 00-9-9 9 9 0 00-6 2.3L3 13" />
      </svg>
    ),
    iconBg: 'bg-slate-600',
    tip: 'Changes auto-save, so you can always undo!',
  },
  {
    id: 'settings',
    type: 'spotlight',
    target: '[data-tour="settings-toggle"]',
    position: 'bottom',
    title: 'Settings Panel',
    description: 'Customize your editing experience.',
    details: 'Adjust fonts, text size, display options, and chunk settings. Settings are saved per project.',
    icon: (
      <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
        <line x1="4" y1="21" x2="4" y2="14" />
        <line x1="4" y1="10" x2="4" y2="3" />
        <line x1="12" y1="21" x2="12" y2="12" />
        <line x1="12" y1="8" x2="12" y2="3" />
        <line x1="20" y1="21" x2="20" y2="16" />
        <line x1="20" y1="12" x2="20" y2="3" />
        <line x1="1" y1="14" x2="7" y2="14" />
        <line x1="9" y1="8" x2="15" y2="8" />
        <line x1="17" y1="16" x2="23" y2="16" />
      </svg>
    ),
    iconBg: 'bg-violet-500',
    desktopOnly: true,
    mobileAlternative: {
      type: 'modal',
      title: 'Settings',
      description: 'Settings are available on desktop.',
      details: 'Open the editor on a computer to access advanced settings like fonts, chunk options, and display preferences.',
    },
  },
  {
    id: 'export',
    type: 'spotlight',
    target: '[data-tour="export-button"]',
    position: 'bottom',
    title: 'Export Your Work',
    description: 'Download in multiple formats.',
    details: 'Export as SRT for most video editors, or TXT for plain text. You can also export for Premiere Pro, DaVinci Resolve, CapCut, or Final Cut Pro — we download an SRT and show import steps.',
    icon: (
      <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
        <path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4" />
        <polyline points="7 10 12 15 17 10" />
        <line x1="12" y1="15" x2="12" y2="3" />
      </svg>
    ),
    iconBg: 'bg-emerald-500',
    tip: 'SRT works with Premiere, DaVinci, CapCut, Final Cut, and most other editors!',
  },
  {
    id: 'audio-player',
    type: 'spotlight',
    target: '[data-tour="audio-player"]',
    position: 'top',
    title: 'Audio Player',
    description: 'Control playback while editing.',
    details: 'Play, pause, and seek through your audio. Click on any word in the editor to jump to that timestamp. Use keyboard shortcuts for faster editing.',
    icon: (
      <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
        <polygon points="5 3 19 12 5 21 5 3" />
      </svg>
    ),
    iconBg: 'bg-blue-500',
    tip: 'Space to play/pause, ← → to go to next word.',
  },
  {
    id: 'editing',
    type: 'modal',
    title: 'Editing Words',
    description: 'Click on any word to edit it.',
    details: 'In Simple view, click a word to switch to Studio view and edit. In Studio view, click directly on words to change text, timing, or choose alternative transcriptions.',
    icon: (
      <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
        <path d="M12 20h9" />
        <path d="M16.5 3.5a2.121 2.121 0 013 3L7 19l-4 1 1-4L16.5 3.5z" />
      </svg>
    ),
    iconBg: 'bg-amber-500',
    tip: 'Purple underlined words have alternative transcriptions.',
  },
  {
    id: 'complete',
    type: 'modal',
    title: "You're All Set! 🎉",
    description: 'Start editing your transcription.',
    details: 'Review your transcript, make corrections, and export when ready. Your changes are saved automatically. Happy editing!',
    icon: <CheckIcon />,
    iconBg: 'bg-gradient-to-br from-emerald-400 to-emerald-600',
  },
];

// Editor Tour Component
const EditorTour = ({ isOpen, onComplete, onSkip }) => {
  const [currentStep, setCurrentStep] = useState(0);
  const [targetRect, setTargetRect] = useState(null);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 1024);

  // Check for mobile on resize
  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 1024);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const rawStep = EDITOR_TOUR_STEPS[currentStep];
  
  // On mobile, use mobileAlternative if step is desktopOnly
  const step = (isMobile && rawStep.desktopOnly && rawStep.mobileAlternative)
    ? { ...rawStep, ...rawStep.mobileAlternative }
    : rawStep;

  // Reset step when tour opens
  useEffect(() => {
    if (isOpen) {
      setCurrentStep(0);
    }
  }, [isOpen]);

  // Find and highlight target element
  const updateTargetRect = useCallback(() => {
    if (isMobile && rawStep.desktopOnly) {
      setTargetRect(null);
      return;
    }
    
    if (step.type === 'spotlight' && step.target) {
      const element = document.querySelector(step.target);
      if (element) {
        const rect = element.getBoundingClientRect();
        setTargetRect(rect);
        element.scrollIntoView({ behavior: 'auto', block: 'center' });
      } else {
        setTargetRect(null);
      }
    } else {
      setTargetRect(null);
    }
  }, [step, rawStep, isMobile]);

  useEffect(() => {
    if (isOpen) {
      updateTargetRect();
      window.addEventListener('scroll', updateTargetRect, true);
      window.addEventListener('resize', updateTargetRect);
      return () => {
        window.removeEventListener('scroll', updateTargetRect, true);
        window.removeEventListener('resize', updateTargetRect);
      };
    }
  }, [isOpen, currentStep, updateTargetRect]);

  // Keyboard navigation
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (!isOpen) return;
      if (e.key === 'Escape') {
        handleSkip();
      } else if (e.key === (isRTL() ? 'ArrowLeft' : 'ArrowRight') || e.key === 'Enter') {
        handleNext();
      } else if (e.key === (isRTL() ? 'ArrowRight' : 'ArrowLeft')) {
        handlePrev();
      }
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, currentStep]);

  const handleNext = () => {
    if (currentStep < EDITOR_TOUR_STEPS.length - 1) {
      setCurrentStep(currentStep + 1);
    }
  };

  const handlePrev = () => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
    }
  };

  const handleSkip = () => {
    try {
      localStorage.setItem('arabcap_editor_tour_skipped', 'true');
    } catch (e) {}
    setCurrentStep(0);
    onSkip?.();
  };

  const handleComplete = () => {
    try {
      localStorage.setItem('arabcap_editor_tour_completed', 'true');
    } catch (e) {}
    setCurrentStep(0);
    onComplete?.();
  };

  if (!isOpen) return null;

  const content = (
    <>
      {/* Dark overlay for modal-type steps, spotlight for spotlight-type */}
      {step.type === 'spotlight' ? (
        <SpotlightOverlay targetRect={targetRect} />
      ) : (
        <div className={`fixed inset-0 z-[9998] backdrop-blur-sm ${isMobile ? 'bg-slate-900/50' : 'bg-slate-900/70'}`} />
      )}

      {/* Click outside to advance */}
      <div 
        className="fixed inset-0 z-[9999]" 
        onClick={(e) => {
          if (e.target === e.currentTarget) {
            handleNext();
          }
        }}
      />

      {/* Tour card */}
      <TourCard
        step={step}
        currentIndex={currentStep}
        totalSteps={EDITOR_TOUR_STEPS.length}
        onNext={handleNext}
        onPrev={handlePrev}
        onSkip={handleSkip}
        onComplete={handleComplete}
        position={step.position}
        targetRect={targetRect}
      />
    </>
  );

  return createPortal(content, document.body);
};

// Editor Tour trigger button - icon only on mobile
export const EditorTourTriggerButton = ({ onClick }) => {
  const { t } = useI18n();
  return (
  <button
    onClick={onClick}
    className="flex items-center justify-center gap-2 px-2.5 sm:px-4 py-2.5 rounded-full text-sm font-bold text-slate-500 hover:text-[#f73140] bg-white/80 hover:bg-white border border-slate-200 hover:border-[#f73140]/30 transition-all shadow-sm"
    data-tour="editor-tour-button"
  >
    <PlayIcon />
    <span className="hidden sm:inline">{t('Tour')}</span>
  </button>
  );
};

// Helper to check if editor tour should auto-show
export const shouldShowEditorTour = () => {
  try {
    const completed = localStorage.getItem('arabcap_editor_tour_completed');
    const skipped = localStorage.getItem('arabcap_editor_tour_skipped');
    return !completed && !skipped;
  } catch {
    return false;
  }
};

// ============================================
// SETTINGS TOUR
// ============================================

const SETTINGS_TOUR_STEPS = [
  {
    id: 'welcome',
    type: 'modal',
    title: 'Settings Panel 🎨',
    description: 'Customize your editing experience.',
    details: 'These settings control how your transcript looks and behaves. Changes are saved automatically per project.',
    icon: (
      <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
        <line x1="4" y1="21" x2="4" y2="14" />
        <line x1="4" y1="10" x2="4" y2="3" />
        <line x1="12" y1="21" x2="12" y2="12" />
        <line x1="12" y1="8" x2="12" y2="3" />
        <line x1="20" y1="21" x2="20" y2="16" />
        <line x1="20" y1="12" x2="20" y2="3" />
        <line x1="1" y1="14" x2="7" y2="14" />
        <line x1="9" y1="8" x2="15" y2="8" />
        <line x1="17" y1="16" x2="23" y2="16" />
      </svg>
    ),
    iconBg: 'bg-gradient-to-br from-violet-500 to-purple-600',
    tip: 'Changes apply instantly to your editor.',
  },
  {
    id: 'behavior',
    type: 'spotlight',
    target: '[data-tour="settings-behavior"]',
    position: 'left',
    title: 'Auto Follow',
    description: 'Keep the active word in view.',
    details: 'When enabled, the editor automatically scrolls to keep the current word visible while playing audio. Great for reviewing!',
    icon: (
      <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
        <circle cx="12" cy="12" r="10" />
        <path d="M12 8v4l3 3" />
      </svg>
    ),
    iconBg: 'bg-blue-500',
  },
  {
    id: 'typography',
    type: 'spotlight',
    target: '[data-tour="settings-typography"]',
    position: 'left',
    title: 'Typography',
    description: 'Adjust fonts and text size.',
    details: 'Choose different fonts for Arabic and Latin text separately. Adjust font size for better readability.',
    icon: (
      <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
        <path d="M4 7V4h16v3" />
        <path d="M9 20h6" />
        <path d="M12 4v16" />
      </svg>
    ),
    iconBg: 'bg-indigo-500',
    tip: 'Use larger fonts when reviewing on a big screen.',
  },
  {
    id: 'punctuation',
    type: 'spotlight',
    target: '[data-tour="settings-punctuation"]',
    position: 'left',
    title: 'Punctuation',
    description: 'Show or hide punctuation marks.',
    details: 'Toggle punctuation like ، ؟ ! . in your captions. Useful for cleaner exports or maintaining punctuation for context.',
    icon: (
      <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
        <circle cx="12" cy="18" r="2" />
        <path d="M12 2v12" />
      </svg>
    ),
    iconBg: 'bg-amber-500',
  },
  {
    id: 'chunks',
    type: 'spotlight',
    target: '[data-tour="settings-chunks"]',
    position: 'left',
    title: 'Chunk Settings',
    description: 'Control how words are grouped.',
    details: 'Chunks determine how words appear in Studio view and exports. Choose words-per-chunk or characters-per-chunk mode. Enable silence-aware or punctuation-aware chunking for smarter grouping.',
    icon: (
      <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
        <rect x="3" y="3" width="7" height="7" />
        <rect x="14" y="3" width="7" height="7" />
        <rect x="3" y="14" width="7" height="7" />
        <rect x="14" y="14" width="7" height="7" />
      </svg>
    ),
    iconBg: 'bg-emerald-500',
    tip: 'Lower words-per-chunk = more subtitle lines.',
  },
  {
    id: 'complete',
    type: 'modal',
    title: 'All Set! ✨',
    description: 'You now know all the settings.',
    details: 'Experiment with different settings to find what works best for your workflow. Remember, all changes are saved automatically!',
    icon: <CheckIcon />,
    iconBg: 'bg-gradient-to-br from-emerald-400 to-emerald-600',
  },
];

// Settings Tour Component
const SettingsTour = ({ isOpen, onComplete, onSkip }) => {
  const [currentStep, setCurrentStep] = useState(0);
  const [targetRect, setTargetRect] = useState(null);

  const step = SETTINGS_TOUR_STEPS[currentStep];

  // Reset step when tour opens
  useEffect(() => {
    if (isOpen) {
      setCurrentStep(0);
    }
  }, [isOpen]);

  // Find and highlight target element
  const updateTargetRect = useCallback(() => {
    if (step.type === 'spotlight' && step.target) {
      const element = document.querySelector(step.target);
      if (element) {
        const rect = element.getBoundingClientRect();
        setTargetRect(rect);
        element.scrollIntoView({ behavior: 'auto', block: 'center' });
      } else {
        setTargetRect(null);
      }
    } else {
      setTargetRect(null);
    }
  }, [step]);

  useEffect(() => {
    if (isOpen) {
      // Small delay to let the settings panel render
      const timer = setTimeout(updateTargetRect, 0);
      window.addEventListener('scroll', updateTargetRect, true);
      window.addEventListener('resize', updateTargetRect);
      return () => {
        clearTimeout(timer);
        window.removeEventListener('scroll', updateTargetRect, true);
        window.removeEventListener('resize', updateTargetRect);
      };
    }
  }, [isOpen, currentStep, updateTargetRect]);

  // Keyboard navigation
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (!isOpen) return;
      if (e.key === 'Escape') {
        handleSkip();
      } else if (e.key === (isRTL() ? 'ArrowLeft' : 'ArrowRight') || e.key === 'Enter') {
        handleNext();
      } else if (e.key === (isRTL() ? 'ArrowRight' : 'ArrowLeft')) {
        handlePrev();
      }
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, currentStep]);

  const handleNext = () => {
    if (currentStep < SETTINGS_TOUR_STEPS.length - 1) {
      setCurrentStep(currentStep + 1);
    }
  };

  const handlePrev = () => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
    }
  };

  const handleSkip = () => {
    try {
      localStorage.setItem('arabcap_settings_tour_completed', 'true');
    } catch (e) {}
    setCurrentStep(0);
    onSkip?.();
  };

  const handleComplete = () => {
    try {
      localStorage.setItem('arabcap_settings_tour_completed', 'true');
    } catch (e) {}
    setCurrentStep(0);
    onComplete?.();
  };

  if (!isOpen) return null;

  const content = (
    <>
      {/* Dark overlay for modal-type steps, spotlight for spotlight-type */}
      {step.type === 'spotlight' ? (
        <SpotlightOverlay targetRect={targetRect} />
      ) : (
        <div className="fixed inset-0 z-[9998] bg-slate-900/70 backdrop-blur-sm" />
      )}

      {/* Click outside to advance */}
      <div 
        className="fixed inset-0 z-[9999]" 
        onClick={(e) => {
          if (e.target === e.currentTarget) {
            handleNext();
          }
        }}
      />

      {/* Tour card */}
      <TourCard
        step={step}
        currentIndex={currentStep}
        totalSteps={SETTINGS_TOUR_STEPS.length}
        onNext={handleNext}
        onPrev={handlePrev}
        onSkip={handleSkip}
        onComplete={handleComplete}
        position={step.position}
        targetRect={targetRect}
      />
    </>
  );

  return createPortal(content, document.body);
};

// Helper to check if settings tour should auto-show
export const shouldShowSettingsTour = () => {
  try {
    const completed = localStorage.getItem('arabcap_settings_tour_completed');
    return !completed;
  } catch {
    return false;
  }
};

export { ProjectCreationTour, EditorTour, SettingsTour };
export default DashboardTour;
